import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import styles from './Button.module.css';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'link';
type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

/**
 * Classes do botão, sem o botão.
 *
 * Existe porque `<Link><Button/></Link>` põe um `<button>` dentro de um `<a>`:
 * HTML inválido e, na prática, dois controles focáveis empilhados no mesmo
 * alvo — o leitor de tela anuncia duas vezes e o teclado para em cima do outro.
 * Quem navega quer um LINK (que abre em aba nova, que se copia); quem só quer
 * o visual usa isto.
 */
export function classesDeBotao(
  variant: ButtonVariant = 'primary',
  size: ButtonSize = 'md',
  extra?: string,
): string {
  return [styles.root, styles[`variant-${variant}`], styles[`size-${size}`], extra]
    .filter(Boolean)
    .join(' ');
}

/**
 * MCUI Button — mirror from broadcast-frontend atoms.
 * No external lib; styles via CSS Modules + tokens.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      leftIcon,
      rightIcon,
      disabled,
      children,
      className,
      ...rest
    },
    ref,
  ) => {
    const composedClass = [
      styles.root,
      styles[`variant-${variant}`],
      styles[`size-${size}`],
      loading && styles.loading,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <button
        ref={ref}
        className={composedClass}
        disabled={disabled ?? loading}
        aria-busy={loading || undefined}
        {...rest}
      >
        {leftIcon && <span className={styles.icon}>{leftIcon}</span>}
        <span className={styles.label}>{children}</span>
        {rightIcon && <span className={styles.icon}>{rightIcon}</span>}
      </button>
    );
  },
);

Button.displayName = 'Button';
